import { useState, useEffect } from "react";
import { SHOW_COSTING } from "@/lib/features";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Users2, Plus, Pencil, Phone, Mail, MapPin, BadgeCheck, ShoppingCart, RotateCw, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { ImportExportButtons } from "@/components/ImportExportButtons";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { DT } from "@/components/ui/denseTable";
import { ListPagination, usePager } from "@/components/ui/ListPagination";
import { MasterRejectDialog } from "@/components/MasterRejectDialog";
import { ChangeDiff, SUPPLIER_LABELS } from "@/components/ChangeDiff";
import { useT } from "@/lib/i18n";
import { loc } from "@/lib/numerals";
import { TranslatedName } from "@/components/TranslatedName";

function RaisePOForm({ supplierName, bomItems, onCreatePO }) {
  const t = useT();
  const raws = (bomItems || []).filter((b) => b.item_type !== "manufactured" && b.status !== "archived");
  const preferred = raws.filter((b) => (b.supplier || "").toUpperCase() === (supplierName || "").toUpperCase());
  const options = [...preferred, ...raws.filter((b) => !preferred.includes(b))];
  const [open, setOpen] = useState(false);
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const it = raws.find((r) => r.id === itemId);
    if (!it || !(Number(qty) > 0)) return;
    setSaving(true);
    try {
      await onCreatePO({ item_id: it.id, sku: it.sku, name: it.name, unit: it.unit || "pcs", suggested_qty: Number(qty), supplier: supplierName, lead_time_days: it.lead_time_days || 0 });
      setItemId(""); setQty(""); setOpen(false);
    } finally { setSaving(false); }
  };
  if (!open) {
    return <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setOpen(true)} data-testid="supplier-raise-po"><ShoppingCart className="h-3 w-3 mr-1" />{t("suppliers.raisePo")}</Button>;
  }
  return (
    <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-2 bg-slate-50" data-testid="supplier-raise-po-form">
      <SearchableSelect
        value={itemId}
        onValueChange={setItemId}
        placeholder="Select material…"
        searchPlaceholder="Search materials…"
        emptyText="No materials found."
        className="flex-1"
        triggerClassName="h-8 text-xs bg-white"
        options={options.map((o) => ({ value: o.id, label: `${o.name} (${o.sku})${(o.supplier || "").toUpperCase() === (supplierName || "").toUpperCase() ? " · preferred" : ""}`, keywords: `${o.name} ${o.sku}` }))}
        testid="supplier-po-item"
      />
      <Input type="number" min="1" placeholder="Qty" value={qty} onChange={(e) => setQty(e.target.value)} className="w-24 h-8 text-xs" data-testid="supplier-po-qty" />
      <Button size="sm" className="h-8 text-[11px] bg-blue-600 hover:bg-blue-700" disabled={!itemId || !(Number(qty) > 0) || saving} onClick={submit} data-testid="supplier-po-submit">Order</Button>
      <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => setOpen(false)}>Cancel</Button>
    </div>
  );
}

function DetailDialog({ sid, bomItems, onCreatePO, onReorder, onClose }) {
  const [d, setD] = useState(null);
  const [reorderPo, setReorderPo] = useState(null);
  const [saving, setSaving] = useState(false);
  const openReorder = (p) => setReorderPo({ id: p.id, po_number: p.po_number, items: p.items.map((i) => ({ item_id: i.item_id, name: i.name, sku: i.sku, unit: i.unit, qty: Number(i.ordered_qty) })) });
  const submitReorder = async () => {
    setSaving(true);
    try {
      await onReorder(reorderPo.id, reorderPo.items.filter((i) => Number(i.qty) > 0).map((i) => ({ item_id: i.item_id, ordered_qty: Number(i.qty) })));
      setReorderPo(null); onClose();
    } catch (err) { logger.error("PO reorder failed (toast shown by action):", err); }
    finally { setSaving(false); }
  };
  useEffect(() => {
    let ignore = false;
    api.get(`/suppliers/${sid}/detail`)
      .then((r) => { if (!ignore) setD(r); })
      .catch((e) => { if (!ignore) { logger.error("Failed to load supplier detail:", e); setD(false); } });
    return () => { ignore = true; };
  }, [sid]);
  return (
    <>
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="supplier-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{d?.supplier?.name || "Supplier"}</DialogTitle>
          <DialogDescription className="text-xs">Purchase orders, on-time history and volume in one place.</DialogDescription>
        </DialogHeader>
        {d === null ? <p className="text-sm text-slate-400 py-6 text-center">Loading…</p> : d === false ? (
          <p className="text-sm text-red-500 py-6 text-center" data-testid="supplier-detail-error">Couldn't load supplier details. Please try again.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["POs", d.po_count], ["On-time", d.on_time_count], ["Late", d.late_count], ["Reliability", `${d.supplier.reliability_score}%`]].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-slate-100 p-2"><p className="text-[10px] text-slate-400 uppercase">{k}</p><p className="font-mono-data font-bold text-slate-800">{v}</p></div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg bg-slate-50 p-2" data-testid="supplier-detail-ordered">Units ordered: <b className="font-mono-data">{d.units_ordered.toLocaleString("en-IN")}</b></div>
              <div className="rounded-lg bg-slate-50 p-2">Units received: <b className="font-mono-data">{d.units_received.toLocaleString("en-IN")}</b></div>
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-100">
              <table className="w-full text-xs"><tbody className="divide-y divide-slate-100">
                {d.pos.length === 0 && <tr><td className="p-3 text-slate-400 text-center">No purchase orders for this supplier yet.</td></tr>}
                {d.pos.map((p) => (
                  <tr key={p.id} data-testid={`detail-po-${p.po_number}`}>
                    <td className="px-3 py-1.5 font-mono-data font-semibold">{p.po_number}</td>
                    <td className="px-3 py-1.5"><span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100">{p.status}</span></td>
                    <td className="px-3 py-1.5 text-slate-500">{p.items.map((i) => `${i.name} ×${i.ordered_qty}`).join(", ")}</td>
                    {onReorder && (
                      <td className="px-3 py-1.5 text-right">
                        <Button size="sm" variant="outline" className="h-6 text-[10px]"
                          disabled={["Pending", "Pending Approval", "Partial"].includes(p.status)}
                          title={["Pending", "Pending Approval", "Partial"].includes(p.status) ? "This PO is still open — receive or cancel it first" : "Reorder these items"}
                          onClick={() => openReorder(p)} data-testid={`reorder-po-${p.po_number}`}>
                          <RotateCw className="h-3 w-3 mr-1" />Reorder
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody></table>
            </div>
            {d.po_count > d.pos.length && (
              <p className="text-[10px] text-slate-400 text-right" data-testid="supplier-detail-more">Showing latest {d.pos.length} of {d.po_count} purchase orders.</p>
            )}
            {onCreatePO && d.supplier?.name && (
              <div className="pt-1"><RaisePOForm supplierName={d.supplier.name} bomItems={bomItems} onCreatePO={onCreatePO} /></div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
    {reorderPo && (
      <Dialog open onOpenChange={(v) => !v && setReorderPo(null)}>
        <DialogContent className="max-w-md" data-testid="reorder-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reorder — {reorderPo.po_number}</DialogTitle>
            <DialogDescription className="text-xs">Adjust quantities before raising the new purchase order.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {reorderPo.items.map((it, idx) => (
              <div key={it.item_id} className="flex items-center gap-2" data-testid={`reorder-item-${it.sku}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{it.name}</p>
                  <p className="text-[10px] text-slate-400 font-mono-data">{it.sku}</p>
                </div>
                <Input type="number" min="0" step="any" value={it.qty} className="h-8 w-24 text-right font-mono-data"
                  data-testid={`reorder-qty-${it.sku}`}
                  onChange={(e) => setReorderPo((prev) => { const items = [...prev.items]; items[idx] = { ...items[idx], qty: e.target.value }; return { ...prev, items }; })} />
                <span className="text-[11px] text-slate-400 w-8">{it.unit}</span>
              </div>
            ))}
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setReorderPo(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={saving} onClick={submitReorder} data-testid="reorder-submit">
              {saving ? "Raising…" : "Raise Reorder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}

const EMPTY = { name: "", gst_tax_id: "", contact_person: "", phone: "", email: "", address: "", categories: "", payment_terms: "", default_lead_time_days: "", notes: "" };

export function SupplierDialog({ supplier, resubmit = false, needsReview = false, onClose, onSave }) {
  const init = supplier
    ? { ...supplier, categories: (supplier.categories || []).join(", "), default_lead_time_days: supplier.default_lead_time_days || "" }
    : EMPTY;
  const [f, setF] = useState(init);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!f.name.trim()) return;
    const payload = {
      name: f.name.trim(), gst_tax_id: f.gst_tax_id, contact_person: f.contact_person, phone: f.phone, email: f.email,
      address: f.address, payment_terms: f.payment_terms, notes: f.notes,
      categories: (f.categories || "").split(",").map((x) => x.trim()).filter(Boolean),
      default_lead_time_days: f.default_lead_time_days === "" ? 0 : Number(f.default_lead_time_days),
    };
    if (supplier) payload.code = f.code;
    await onSave(supplier, payload);
    onClose();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="supplier-master-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{supplier ? (resubmit ? `Edit & Resubmit ${supplier.name}` : `Edit ${supplier.name}`) : "New Supplier"}</DialogTitle>
          <DialogDescription className="text-xs">
            {resubmit
              ? "Update the record and send it back for admin approval."
              : needsReview
                ? "Maintain the supplier master record used across purchasing. New records — and edits to Active records — go to Pending Approval until an admin approves the changes."
                : "Maintain the supplier master record used across purchasing."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <div className="space-y-1"><Label className="text-xs">Name * (saved as UPPERCASE)</Label><Input value={f.name} onChange={set("name")} data-testid="supplier-name-input" /></div>
          <div className="space-y-1"><Label className="text-xs">GST / Tax ID</Label><Input value={f.gst_tax_id} onChange={set("gst_tax_id")} data-testid="supplier-gst-input" /></div>
          {supplier && <div className="space-y-1"><Label className="text-xs">Supplier Code</Label><Input value={f.code || ""} readOnly className="bg-slate-50" data-testid="supplier-code-input" /></div>}
          {!supplier && <div className="space-y-1"><Label className="text-xs">Supplier Code</Label><Input value="Auto (SUP-####)" readOnly className="bg-slate-50 text-slate-400" data-testid="supplier-code-auto" /></div>}
          <div className="space-y-1"><Label className="text-xs">Contact Person</Label><Input value={f.contact_person} onChange={set("contact_person")} data-testid="supplier-contact-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="supplier-phone-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={f.email} onChange={set("email")} data-testid="supplier-email-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Default Lead Time (Days)</Label><Input type="number" min="0" value={f.default_lead_time_days} onChange={set("default_lead_time_days")} data-testid="supplier-lead-input" /></div>
          <div className="space-y-1 col-span-2"><Label className="text-xs">Address</Label><Input value={f.address} onChange={set("address")} data-testid="supplier-address-input" /></div>
          <div className="space-y-1 col-span-2"><Label className="text-xs">Category / Material Tags (Comma-Separated)</Label><Input value={f.categories} onChange={set("categories")} placeholder="Fasteners, Bearings" data-testid="supplier-categories-input" /></div>
          {SHOW_COSTING && <div className="space-y-1 col-span-2"><Label className="text-xs">Payment Terms</Label><Input value={f.payment_terms} onChange={set("payment_terms")} placeholder="Net 30" data-testid="supplier-terms-input" /></div>}
          <div className="space-y-1 col-span-2"><Label className="text-xs">Notes</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="supplier-notes-input" /></div>
        </div>
        <DialogFooter className="mt-4">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className={`h-8 text-xs ${resubmit ? "bg-purple-600 hover:bg-purple-700" : "bg-blue-600 hover:bg-blue-700"}`} onClick={save} data-testid="supplier-master-save">
            {resubmit ? "Resubmit for Approval" : supplier ? "Save Changes" : "Create Supplier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_BADGE = {
  "Pending Approval": "bg-purple-50 text-purple-700",
  Rejected: "bg-rose-50 text-rose-700",
};

export default function SuppliersPanel({ suppliers, isAdmin, canManage = false, currentEmail, bomItems = [], onCreatePO, onReorder, onCreate, onUpdate, onApprove, onReject, onResubmit, onImported }) {
  const t = useT();
  const [dialog, setDialog] = useState(null); // {new:true} | {supplier} | {supplier, resubmit:true}
  const [rejecting, setRejecting] = useState(null);
  const [detailSid, setDetailSid] = useState(null);
  const list = suppliers || [];
  const { pageItems, pagerProps } = usePager(list);
  const rel = (v) => (v >= 90 ? "text-emerald-600" : v >= 70 ? "text-amber-600" : "text-red-600");
  const statusOf = (s) => s.status || (s.active !== false ? "Active" : "Inactive");
  const canReview = (s) => canManage && (!s.created_by || s.created_by === currentEmail); // planners manage their own submissions
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="suppliers-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Users2 className="h-4 w-4 text-blue-600" />
        <h3 className="font-display font-semibold text-slate-800">{t("suppliers.title")}</h3>
        <span className="text-xs text-slate-400" data-testid="supplier-count">{loc(String(list.filter((s) => s.active !== false).length))} {t("common.active")} · {loc(String(list.length))} {t("common.total")}</span>
        {(isAdmin || canManage) && (
          <div className="ml-auto flex items-center gap-1.5">
            {isAdmin && <ImportExportButtons entity="suppliers" label="suppliers" onImported={onImported} />}
            <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700" onClick={() => setDialog({ new: true })} data-testid="add-supplier-btn">
              <Plus className="h-3 w-3 mr-1" /> {t("suppliers.add")}
            </Button>
          </div>
        )}
      </div>
      <div className={DT.wrap}>
        <table className={`${DT.table} min-w-[860px]`}>
          <thead className={DT.thead}>
            <tr className={DT.headerRow}>
              <th className="text-left font-semibold px-3 py-2">{t("col.supplier")}</th>
              <th className="text-left font-semibold px-3 py-2">{t("common.contact")}</th>
              <th className="text-left font-semibold px-3 py-2">{t("col.categories")}</th>
              <th className="text-right font-semibold px-3 py-2">{t("col.lead")}</th>
              {SHOW_COSTING && <th className="text-left font-semibold px-3 py-2">{t("col.terms")}</th>}
              <th className="text-right font-semibold px-3 py-2">{t("col.reliability")}</th>
              <th className="text-center font-semibold px-3 py-2">{t("common.status")}</th>
              <th className="text-right font-semibold px-3 py-2">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.length === 0 && (
              <tr><td colSpan={SHOW_COSTING ? 8 : 7} className="px-3 py-8 text-center text-slate-400" data-testid="suppliers-empty">{t("suppliers.empty")}</td></tr>
            )}
            {pageItems.map((s) => {
              const st = statusOf(s);
              return (
              <tr key={s.id} className={`border-b border-slate-100 transition-colors duration-150 ${st === "Rejected" ? "bg-rose-50/70 hover:bg-rose-100/80" : st === "Pending Approval" ? "bg-yellow-50/70 hover:bg-yellow-100/80" : `even:bg-slate-50/60 hover:bg-slate-100/80 ${s.active === false ? "opacity-50" : ""}`}`} data-testid={`supplier-row-${s.id}`}>
                <td className="px-3 py-2">
                  <button className="font-semibold text-slate-800 hover:text-blue-600 hover:underline text-left" onClick={() => setDetailSid(s.id)} data-testid={`supplier-detail-btn-${s.id}`}><TranslatedName text={s.name} /></button>
                  {s.code && <p className="text-[10px] text-slate-400 font-mono-data">{s.code}{s.gst_tax_id ? ` · ${s.gst_tax_id}` : ""}</p>}
                  {st === "Rejected" && s.rejection_reason && (
                    <p className="text-[10px] text-rose-600 italic mt-0.5" data-testid={`supplier-rejection-${s.id}`}>Rejected: {s.rejection_reason}</p>
                  )}
                  {st === "Pending Approval" && (
                    <p className="text-[10px] text-purple-500 mt-0.5" data-testid={`supplier-pending-${s.id}`}>Awaiting admin approval{s.created_by ? ` · raised by ${s.created_by}` : ""}</p>
                  )}
                  {(st === "Pending Approval" || st === "Rejected") && (
                    <ChangeDiff changes={s.pending_changes} labels={SUPPLIER_LABELS} testidPrefix={`supplier-${s.id}`} />
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {s.contact_person && <p className="font-medium">{s.contact_person}</p>}
                  <p className="flex items-center gap-1 text-[11px]"><Phone className="h-3 w-3" />{s.phone || "—"}</p>
                  <p className="flex items-center gap-1 text-[11px] truncate max-w-[180px]"><Mail className="h-3 w-3" />{s.email || "—"}</p>
                  {s.address && <p className="flex items-center gap-1 text-[10px] text-slate-400 truncate max-w-[180px]"><MapPin className="h-3 w-3" />{s.address}</p>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(s.categories || []).map((c) => <span key={c} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">{c}</span>)}
                    {(!s.categories || s.categories.length === 0) && <span className="text-slate-300">—</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono-data text-slate-600">{s.default_lead_time_days ? `${s.default_lead_time_days}d` : "—"}</td>
                {SHOW_COSTING && <td className="px-3 py-2 text-slate-600">{s.payment_terms || "—"}</td>}
                <td className={`px-3 py-2 text-right font-mono-data font-bold ${rel(s.reliability_score)}`} data-testid={`supplier-reliability-${s.id}`}>
                  {s.reliability_score}%
                  <span className="block text-[9px] font-normal text-slate-400">{s.on_time_count || 0}/{(s.on_time_count || 0) + (s.late_count || 0)} on-time</span>
                </td>
                <td className="px-3 py-2 text-center">
                  {STATUS_BADGE[st] ? (
                    <span className={`inline-block px-2 py-0.5 rounded font-mono-data text-[10px] ${STATUS_BADGE[st]}`} data-testid={`supplier-status-${s.id}`}>{st}</span>
                  ) : isAdmin ? (
                    <Switch checked={s.active !== false} onCheckedChange={(v) => onUpdate(s, { active: v })} data-testid={`supplier-active-${s.id}`} />
                  ) : (
                    <span className={`inline-flex items-center gap-1 text-[10px] ${s.active !== false ? "text-emerald-600" : "text-slate-400"}`}>
                      <BadgeCheck className="h-3 w-3" />{s.active !== false ? t("common.active") : t("common.inactive")}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {isAdmin && st === "Pending Approval" && (
                      <>
                        <Button size="sm" className="h-7 text-[11px] bg-purple-600 hover:bg-purple-700" onClick={() => onApprove(s)} data-testid={`approve-supplier-${s.id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> {t("common.approve")}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-600 border-rose-200" onClick={() => setRejecting(s)} data-testid={`reject-supplier-${s.id}`}>
                          <XCircle className="h-3 w-3 mr-1" /> {t("common.reject")}
                        </Button>
                      </>
                    )}
                    {canManage && !isAdmin && st === "Rejected" && canReview(s) && (
                      <Button size="sm" className="h-7 text-[11px] bg-purple-600 hover:bg-purple-700" onClick={() => setDialog({ supplier: s, resubmit: true })} data-testid={`resubmit-supplier-${s.id}`}>
                        <RotateCcw className="h-3 w-3 mr-1" /> {t("common.resubmit")}
                      </Button>
                    )}
                    {(isAdmin || (canManage && st !== "Rejected" && (st === "Active" || canReview(s)))) && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setDialog({ supplier: s })} data-testid={`edit-supplier-${s.id}`}>
                        <Pencil className="h-3 w-3 mr-1" /> {t("common.edit")}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        <ListPagination {...pagerProps} testid="suppliers-pagination" />
      </div>
      {dialog && (
        <SupplierDialog
          supplier={dialog.supplier || null}
          resubmit={!!dialog.resubmit}
          needsReview={!isAdmin && canManage}
          onClose={() => setDialog(null)}
          onSave={(existing, payload) => (existing ? (dialog.resubmit ? onResubmit(existing, payload) : onUpdate(existing, payload)) : onCreate(payload))}
        />
      )}
      {rejecting && (
        <MasterRejectDialog
          title={`Reject ${rejecting.name}`}
          onClose={() => setRejecting(null)}
          onConfirm={(reason) => onReject(rejecting, reason)}
        />
      )}
      {detailSid && <DetailDialog sid={detailSid} bomItems={bomItems} onCreatePO={onCreatePO} onReorder={onReorder} onClose={() => setDetailSid(null)} />}
    </div>
  );
}
